import type {
  ITransition,
  ITypewriterTransitionSettings,
  TransitionType,
} from "./Transition";
import { InteractionType } from "./Interaction";

export const DEFAULT_TRANSITION_DURATION = 2;
export const DEFAULT_TYPEWRITER_FONT_SIZE = 50;
export const DEFAULT_TYPEWRITER_TRANSITION_SPEED_START = 0.5;
export const DEFAULT_TYPEWRITER_TRANSITION_SPEED_STOP = 0.5;
export const DEFAULT_TYPEWRITER_TYPING_SPEED = 1;
export const DEFAULT_TYPEWRITER_SHOW_BUTTON_DELAY = 0;

const DEFAULT_TRANSITION_COLOR = { r: 0, g: 0, b: 0, a: 1 };

export function createDefaultTypewriterSettings(): ITypewriterTransitionSettings {
  return {
    instantText: false,
    title: "",
    text: "",
    titleIconAssetId: "",
    bottomIconAssetId: "",
    fontSize: DEFAULT_TYPEWRITER_FONT_SIZE,
    transitionSpeedStart: DEFAULT_TYPEWRITER_TRANSITION_SPEED_START,
    transitionSpeedStop: DEFAULT_TYPEWRITER_TRANSITION_SPEED_STOP,
    typingSpeed: DEFAULT_TYPEWRITER_TYPING_SPEED,
    showButtonDelay: DEFAULT_TYPEWRITER_SHOW_BUTTON_DELAY,
    useBackgroundColor: false,
    backgroundColor: { ...DEFAULT_TRANSITION_COLOR },
    useTextColor: false,
    textColor: { ...DEFAULT_TRANSITION_COLOR },
  };
}

export function normalizeTypewriterSettings(
  settings?: Partial<ITypewriterTransitionSettings>
): ITypewriterTransitionSettings {
  const defaults = createDefaultTypewriterSettings();
  return {
    ...settings,
    instantText: settings?.instantText ?? defaults.instantText,
    title: settings?.title ?? defaults.title,
    text: settings?.text ?? defaults.text,
    titleIconAssetId: settings?.titleIconAssetId ?? defaults.titleIconAssetId,
    bottomIconAssetId:
      settings?.bottomIconAssetId ?? defaults.bottomIconAssetId,
    fontSize: settings?.fontSize ?? defaults.fontSize,
    transitionSpeedStart:
      settings?.transitionSpeedStart ?? defaults.transitionSpeedStart,
    transitionSpeedStop:
      settings?.transitionSpeedStop ?? defaults.transitionSpeedStop,
    typingSpeed: settings?.typingSpeed ?? defaults.typingSpeed,
    showButtonDelay: settings?.showButtonDelay ?? defaults.showButtonDelay,
    useBackgroundColor:
      settings?.useBackgroundColor ?? defaults.useBackgroundColor,
    backgroundColor: settings?.backgroundColor ?? defaults.backgroundColor,
    useTextColor: settings?.useTextColor ?? defaults.useTextColor,
    textColor: settings?.textColor ?? defaults.textColor,
  };
}

export function normalizeTransition(
  transition?: Partial<ITransition>
): ITransition {
  return {
    ...transition,
    id: transition?.id ?? "",
    name: transition?.name ?? "Transition",
    type: transition?.type ?? InteractionType.Transition,
    text: transition?.text ?? "",
    assetId: transition?.assetId ?? "",
    color: transition?.color ?? { ...DEFAULT_TRANSITION_COLOR },
    duration: transition?.duration ?? DEFAULT_TRANSITION_DURATION,
    transitionType: transition?.transitionType ?? "default",
    typewriter: normalizeTypewriterSettings(transition?.typewriter),
  };
}

/**
 * Returns the transition with the given type. When switching to "typewriter",
 * reserves localization keys (via getKeys) for any typewriter title/text that
 * is still "" so the editors can write localized values right away.
 */
export function withTransitionType(
  transition: ITransition,
  type: TransitionType,
  getKeys: (amount: number) => string[]
): ITransition {
  let typewriter = transition.typewriter;

  if (type === "typewriter") {
    const missing =
      (typewriter.title === "" ? 1 : 0) + (typewriter.text === "" ? 1 : 0);
    if (missing > 0) {
      const keys = getKeys(missing);
      let i = 0;
      typewriter = {
        ...typewriter,
        title: typewriter.title === "" ? keys[i++] : typewriter.title,
        text: typewriter.text === "" ? keys[i++] : typewriter.text,
      };
    }
  }

  return {
    ...transition,
    transitionType: type,
    typewriter: typewriter,
  };
}
